import { useSelector } from '@xstate/react';
import { createContext, useContext } from 'react';
import { ActorRefFrom } from 'xstate';
import { negotiationMachine } from './negotiation.machine';
import type { NegotiationResponseType } from './types';

type NegotiationActorRef = ActorRefFrom<typeof negotiationMachine>;
export const NegotiationRefContext = createContext<NegotiationActorRef | null>(null);

export function useNegotiationActorRef() {
  const ref = useContext(NegotiationRefContext);
  if (!ref) throw new Error('useNegotiationActorRef must be used within Negotiation.Provider');
  return ref;
}

// [MCV-1645/MCV-1646] Whether the tabs' form inputs should render as
// disabled: any view mode other than 'draft'/'actionable' (loading, waiting
// on the other party, a historical version, or mid-submit).
export type NegotiationEditingContext = {
  inputsDisabled: boolean;
  responseType: NegotiationResponseType;
};

const NegotiationEditingReactContext = createContext<NegotiationEditingContext>({
  inputsDisabled: false,
  responseType: 'msaas',
});

export const NegotiationEditingContext = NegotiationEditingReactContext;

export function useNegotiationInputsDisabled(): boolean {
  return useContext(NegotiationEditingReactContext).inputsDisabled;
}

export function useNegotiationResponseType(): NegotiationResponseType {
  return useContext(NegotiationEditingReactContext).responseType;
}

// [MCV-1646] The "previous terms" comparison column and change-detection
// baseline: a draft's own source-of-truth terms while drafting, otherwise the
// persisted negotiation being reviewed/countered.
export function useNegotiationBaseline() {
  const negotiationRef = useNegotiationActorRef();
  return useSelector(
    negotiationRef,
    (state) => state.context.draft?.terms ?? state.context.negotiation
  );
}
